import { URL_MANAGE_JOB, URL_ROOT } from '@seek/adv-constants';
import { formatMinorUnits } from '@seek/currency';
import { useMelwaysLink } from '@seek/melways-react';
import type { Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  ButtonLink,
  Heading,
  Inline,
  Notice,
  Stack,
  Strong,
  Text,
  TextLink,
} from 'braid-design-system';
import React, { Fragment, useEffect, useMemo } from 'react';

import { useAppContext } from 'src/App/context/AppContext';
import { useTracking } from 'src/App/context/TrackingProvider';
import type { AdBudgetType } from 'src/App/generated/graphql-types';
import type { AdvertiserContract } from 'src/App/types/advertiser';
import { mapAdBudgetType } from 'src/App/utils/analytics/event';
import { centsToDollars } from 'src/App/utils/price';

import { useStore } from '../../context/provider';
import type { AdBudgetInfo } from '../../context/reducer';
import { createCheckoutPageOnLoadBaseEvent } from '../../eventTracking/createEvents';

import translations from './.vocab';

interface ConfirmationProps {
  displayItemTitle: string;
  discountDurationMonths: number;
  itemPrice: number;
  currency: string;
  adBudgetType: AdBudgetType;
  advertiserId?: string;
  userId?: string;
  advertiserContracts?: AdvertiserContract[];
  productSetId?: string;
  adBudgetInfo: AdBudgetInfo;
  adBudgetList?: AdBudgetInfo[];
}

export default function Confirmation({
  displayItemTitle,
  discountDurationMonths,
  itemPrice: totalCents,
  currency,
  advertiserId,
  advertiserContracts,
  productSetId,
  adBudgetInfo,
  adBudgetList,
}: ConfirmationProps) {
  const { language } = useAppContext();
  const localize = useMelwaysLink();
  const {
    isPaidByStoredCreditCard,
    isSaveCreditCardChecked,
    paymentMethodSelected,
    purchasePaymentReferenceNumber,
  } = useStore().state;
  const { trackView, trackLink } = useTracking();

  const onClickBaseEvent = useMemo(
    () => ({
      productEvents: 'purchase',
      adBudgetPrices: [centsToDollars(adBudgetInfo.price)],
      adBudgetType: mapAdBudgetType(adBudgetInfo.ads),
      paymentMethod: paymentMethodSelected,
      isSaveCreditCardChecked,
      isPaidByStoredCreditCard,
      purchasePaymentReferenceNumber,
    }),
    [
      adBudgetInfo.ads,
      adBudgetInfo.price,
      isPaidByStoredCreditCard,
      isSaveCreditCardChecked,
      paymentMethodSelected,
      purchasePaymentReferenceNumber,
    ],
  );

  const currentPage = 'seek contract purchase';
  const baseEvent = useMemo(
    () =>
      createCheckoutPageOnLoadBaseEvent(
        {
          advertiserContracts,
          productSetId: productSetId ?? '',
          productEvents: 'purchase',
          adBudgetList,
        },
        adBudgetInfo,
        currentPage,
      ),
    [adBudgetInfo, advertiserContracts, productSetId, adBudgetList],
  );

  useEffect(() => {
    if (adBudgetInfo && advertiserId && baseEvent) {
      trackView({
        eventName: 'seek_contract_purchase_displayed',
        data: {
          ...baseEvent,
          adBudgetType: mapAdBudgetType(adBudgetInfo.ads),
          paymentMethod: paymentMethodSelected ?? '',
          contractProductName: adBudgetInfo.name,
          productTotalPrice: [centsToDollars(adBudgetInfo.price)],
          purchasePaymentReferenceNumber: purchasePaymentReferenceNumber ?? '',
          isSaveCreditCardChecked: Boolean(isSaveCreditCardChecked),
          isPaidByStoredCreditCard: Boolean(isSaveCreditCardChecked),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adBudgetInfo, advertiserId, baseEvent]);

  const { t } = useTranslations(translations);

  return (
    <Fragment>
      <Stack space="large">
        <Heading level="1" align="center">
          {t('order confirmation')}
        </Heading>
        <Box paddingY="gutter" paddingX="gutter" background="surface">
          <Stack space="large">
            <Box display="inline">
              <Inline alignY="bottom" space="xxsmall">
                <Notice tone="positive">
                  <Text>{t('nice one. You are now...')}</Text>
                </Notice>
              </Inline>
            </Box>

            <Text>
              {t('you will receive a purchase...')}{' '}
              <TextLink
                hitArea="large"
                href={localize({
                  path: URL_ROOT,
                  language: language as Language,
                })}
                onClick={() => {
                  if (adBudgetInfo) {
                    trackLink({
                      eventName: 'home_page_link_clicked',
                      data: {
                        ...baseEvent,
                        ...onClickBaseEvent,
                        currentPage: 'seek contract purchase',
                      },
                    });
                  }
                }}
              >
                {t('home page')}
              </TextLink>
              .
            </Text>

            <Box
              style={{
                maxWidth: '500px',
              }}
              boxShadow="borderNeutralLight"
              padding="medium"
            >
              <Stack dividers="regular" space="none">
                <Box paddingY="small">
                  <Text weight="strong">{t('details of order')}</Text>
                </Box>

                <Box paddingY="small">
                  <Text>
                    {displayItemTitle}:{' '}
                    <Strong>
                      {formatMinorUnits(totalCents, {
                        currency,
                        locale: language,
                      })}
                    </Strong>
                  </Text>
                </Box>
                <Box paddingY="small">
                  <Text>
                    {t('expiry')}:{' '}
                    <Strong>
                      {discountDurationMonths} {t('months')}
                    </Strong>
                  </Text>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>
        <Stack space="medium">
          <Actions>
            <ButtonLink
              href={localize({
                path: `${URL_MANAGE_JOB}?referrer=createJob`,
                language: language as Language,
              })}
              onClick={() => {
                if (adBudgetInfo) {
                  trackLink({
                    eventName: 'create_job_link_clicked',
                    data: {
                      ...baseEvent,
                      ...onClickBaseEvent,
                      currentPage: 'seek contract purchase',
                    },
                  });
                }
              }}
              tone="brandAccent"
            >
              {t('create a job ad')}
            </ButtonLink>
          </Actions>
        </Stack>
      </Stack>
    </Fragment>
  );
}
